import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  FileText,
  Headphones,
  Image as ImageIcon,
  Loader2,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { MultiSelect } from "@/components/ui/multi-select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { uploadThumbnailToCOS, uploadToCOS } from "@/lib/cos-upload";
import { cn } from "@/lib/utils";
import {
  getAllKnownCategoryOptions,
  normalizeCategoryList,
} from "@/lib/category-config";
import {
  addCustomCategoryOption,
  useCategoryOptions,
} from "@/hooks/useCategoryOptions";

interface AudioUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onSuccess: () => void;
}

interface VttCue {
  startTime: number;
  endTime: number;
  text: string;
}

type ProcessingStep = "idle" | "uploading" | "processing" | "done" | "error";

const JLPT_LEVELS = ["N5", "N4", "N3", "N2", "N1"] as const;
type JlptLevel = (typeof JLPT_LEVELS)[number];

function parseVttTime(timeStr: string): number {
  const parts = timeStr.split(":");
  if (parts.length === 3) {
    return Number(parts[0]) * 3600 + Number(parts[1]) * 60 + Number(parts[2].replace(",", "."));
  }
  if (parts.length === 2) {
    return Number(parts[0]) * 60 + Number(parts[1].replace(",", "."));
  }
  return 0;
}

function parseVTT(content: string): VttCue[] {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const cues: VttCue[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line.includes("-->")) {
      i += 1;
      continue;
    }

    const [start, end] = line.split("-->").map((item) => item.trim());
    const startTime = parseVttTime(start);
    const endTime = parseVttTime(end);

    i += 1;
    const textLines: string[] = [];
    while (i < lines.length && lines[i].trim()) {
      if (!/^\d+$/.test(lines[i].trim())) {
        textLines.push(lines[i].trim());
      }
      i += 1;
    }

    if (textLines.length > 0) {
      cues.push({
        startTime,
        endTime,
        text: textLines.join("\n"),
      });
    }

    i += 1;
  }

  return cues;
}

function estimateJlpt(text: string): JlptLevel {
  const cleaned = text.replace(/\s+/g, "");
  if (!cleaned) return "N5";

  const kanjiCount = (cleaned.match(/[\u4e00-\u9fff]/g) || []).length;
  const ratio = kanjiCount / cleaned.length;

  if (ratio >= 0.35) return "N1";
  if (ratio >= 0.28) return "N2";
  if (ratio >= 0.2) return "N3";
  if (ratio >= 0.12) return "N4";
  return "N5";
}

function stepLabel(step: ProcessingStep): string {
  switch (step) {
    case "uploading":
      return "Uploading audio to COS...";
    case "processing":
      return "Processing subtitles...";
    case "done":
      return "Completed";
    case "error":
      return "Failed";
    default:
      return "";
  }
}

export default function AudioUploadDialog({
  open,
  onOpenChange,
  userId,
  onSuccess,
}: AudioUploadDialogProps) {
  const { toast } = useToast();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const subtitleInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const audioProbeRef = useRef<HTMLAudioElement>(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    jlpt_level: "N5" as JlptLevel,
    category: "story",
    categories: ["story"] as string[],
    is_published: false,
    autoDetectLevel: true,
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [vttFile, setVttFile] = useState<File | null>(null);
  const [vttCues, setVttCues] = useState<VttCue[]>([]);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [audioDuration, setAudioDuration] = useState(0);

  const [step, setStep] = useState<ProcessingStep>("idle");
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  const { data: categoryOptions = getAllKnownCategoryOptions(), refetch: refetchCategoryOptions } =
    useCategoryOptions();

  const isProcessing = step === "uploading" || step === "processing";

  const previewUrl = useMemo(() => {
    if (!selectedFile) return null;
    return URL.createObjectURL(selectedFile);
  }, [selectedFile]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const createCategory = (input: string) => {
    const created = addCustomCategoryOption(input);
    if (!created) return null;

    void refetchCategoryOptions();
    toast({
      title: "Category created",
      description: `${created.label} can now be reused`,
    });

    return created;
  };

  const reset = () => {
    setFormData({
      title: "",
      description: "",
      jlpt_level: "N5",
      category: "story",
      categories: ["story"],
      is_published: false,
      autoDetectLevel: true,
    });
    setSelectedFile(null);
    setVttFile(null);
    setVttCues([]);
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setAudioDuration(0);
    setStep("idle");
    setProgress(0);
    setErrorMessage("");
  };

  const onAudioMeta = () => {
    setAudioDuration(audioProbeRef.current?.duration || 0);
  };

  const handleAudioSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("audio/")) {
      toast({ title: "Please select an audio file", variant: "destructive" });
      return;
    }

    setSelectedFile(file);
    setStep("idle");
    setErrorMessage("");

    if (!formData.title.trim()) {
      setFormData((prev) => ({
        ...prev,
        title: file.name.replace(/\.[^.]+$/, ""),
      }));
    }
  };

  const handleVttSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".vtt")) {
      toast({ title: "Please select a .vtt file", variant: "destructive" });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result || "");
      const cues = parseVTT(content);

      setVttFile(file);
      setVttCues(cues);

      if (formData.autoDetectLevel && cues.length > 0) {
        const level = estimateJlpt(cues.map((cue) => cue.text).join(" "));
        setFormData((prev) => ({
          ...prev,
          jlpt_level: level,
        }));
      }
    };

    reader.readAsText(file);
  };

  const handleThumbSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: "Please select an image", variant: "destructive" });
      return;
    }

    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile || !formData.title.trim()) {
      toast({ title: "Please complete title and file", variant: "destructive" });
      return;
    }

    if (!vttFile || vttCues.length === 0) {
      toast({
        title: "Subtitle required",
        description: "Audio upload requires a .vtt subtitle file",
        variant: "destructive",
      });
      return;
    }

    const normalizedCategories = normalizeCategoryList(formData.categories);
    const category = normalizedCategories[0] || "story";

    setStep("uploading");
    setProgress(1);
    setErrorMessage("");

    const startTime = Date.now();
    let logId: string | null = null;
    let audioId: string | null = null;

    try {
      const { data: logData } = await supabase
        .from("upload_logs")
        .insert({
          uploaded_by: userId,
          file_name: selectedFile.name,
          file_size: selectedFile.size,
          file_type: selectedFile.type,
          status: "uploading",
        })
        .select()
        .single();

      logId = logData?.id || null;

      const audioUpload = await uploadToCOS({
        file: selectedFile,
        folder: "audios",
        onProgress: (value) => setProgress(Math.min(45, Math.round(value))),
      });

      if (!audioUpload.success || !audioUpload.url) {
        throw new Error(audioUpload.error || "Audio upload failed");
      }

      if (logId) {
        await supabase
          .from("upload_logs")
          .update({
            status: "processing",
            cos_key: audioUpload.key,
            cdn_url: audioUpload.url,
          })
          .eq("id", logId);
      }

      let thumbnailUrl: string | null = null;
      if (thumbnailFile) {
        const thumbUpload = await uploadThumbnailToCOS(thumbnailFile);
        if (thumbUpload.success && thumbUpload.url) {
          thumbnailUrl = thumbUpload.url;
        }
      }

      setProgress(60);

      const duration = Math.max(
        Math.ceil(audioDuration || 0),
        Math.ceil(vttCues[vttCues.length - 1]?.endTime || 0),
      );

      const { data: createdAudio, error: insertError } = await supabase
        .from("audios")
        .insert({
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          audio_url: audioUpload.url,
          thumbnail_url: thumbnailUrl,
          jlpt_level: formData.jlpt_level,
          category,
          is_premium: false,
          is_published: false,
          created_by: userId,
          duration,
        })
        .select()
        .single();

      if (insertError || !createdAudio) {
        throw insertError || new Error("Failed to create audio record");
      }

      audioId = createdAudio.id;
      setStep("processing");
      setProgress(72);

      if (logId) {
        await supabase.from("upload_logs").update({ audio_id: audioId }).eq("id", logId);
      }

      const { data: processData, error: processError } = await supabase.functions.invoke(
        "process-audio-subtitles",
        {
          body: {
            audioId,
            cues: vttCues,
          },
        },
      );

      if (processError) {
        throw new Error(processError.message || "Subtitle processing failed");
      }
      if ((processData as any)?.error) {
        throw new Error((processData as any).error);
      }

      setProgress(92);

      if (formData.is_published) {
        const { error: publishError } = await supabase
          .from("audios")
          .update({ is_published: true })
          .eq("id", audioId);

        if (publishError) throw publishError;
      }

      const { count: subtitleCount } = await supabase
        .from("audio_subtitles")
        .select("*", { count: "exact", head: true })
        .eq("audio_id", audioId);

      if (logId) {
        await supabase
          .from("upload_logs")
          .update({
            status: "completed",
            duration_ms: Date.now() - startTime,
            subtitle_count: subtitleCount || vttCues.length,
            subtitle_source: "vtt",
          })
          .eq("id", logId);
      }

      setProgress(100);
      setStep("done");

      toast({
        title: "Audio uploaded",
        description: `${formData.title} uploaded successfully`,
      });

      setTimeout(() => {
        onOpenChange(false);
        reset();
        onSuccess();
      }, 800);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed";
      setStep("error");
      setErrorMessage(message);

      if (logId) {
        await supabase
          .from("upload_logs")
          .update({
            status: "failed",
            duration_ms: Date.now() - startTime,
            error_message: message,
            audio_id: audioId,
          })
          .eq("id", logId);
      }

      toast({ title: "Upload failed", description: message, variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={isProcessing ? undefined : onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Headphones className="h-5 w-5" />
            Upload Audio
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-3">
          {step !== "idle" && (
            <div className="space-y-2 rounded-lg border bg-muted/40 p-3">
              <div className="flex items-center gap-2 text-sm">
                {isProcessing && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                {step === "done" && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                {step === "error" && <AlertCircle className="h-4 w-4 text-destructive" />}
                <span>{stepLabel(step)}</span>
              </div>
              <Progress value={progress} className="h-2" />
              {errorMessage ? <p className="text-xs text-destructive">{errorMessage}</p> : null}
            </div>
          )}

          <div
            className={cn(
              "cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition",
              selectedFile ? "border-primary bg-primary/5" : "hover:border-primary/60",
              isProcessing && "pointer-events-none opacity-50",
            )}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="audio/*"
              onChange={handleAudioSelect}
              disabled={isProcessing}
            />
            {selectedFile ? (
              <div className="flex items-center justify-center gap-2 text-sm">
                <Headphones className="h-4 w-4 text-primary" />
                <span className="break-all">{selectedFile.name}</span>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Click to select audio file</div>
            )}
          </div>

          {previewUrl ? (
            <audio
              ref={audioProbeRef}
              src={previewUrl}
              className="hidden"
              onLoadedMetadata={onAudioMeta}
            />
          ) : null}

          <div
            className={cn(
              "cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition",
              vttFile ? "border-emerald-500 bg-emerald-500/5" : "hover:border-emerald-500/60",
              isProcessing && "pointer-events-none opacity-50",
            )}
            onClick={() => subtitleInputRef.current?.click()}
          >
            <input
              ref={subtitleInputRef}
              type="file"
              className="hidden"
              accept=".vtt"
              onChange={handleVttSelect}
              disabled={isProcessing}
            />
            {vttFile ? (
              <div className="flex items-center justify-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-emerald-600" />
                <span>
                  {vttFile.name} ({vttCues.length} cues)
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={(event) => {
                    event.stopPropagation();
                    setVttFile(null);
                    setVttCues([]);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Upload required .vtt subtitle file</div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Thumbnail (optional)
            </Label>

            {thumbnailPreview ? (
              <div className="relative overflow-hidden rounded-lg border">
                <img src={thumbnailPreview} alt="thumbnail" className="h-32 w-full object-cover" />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-2 h-7 w-7 bg-background/80"
                  onClick={() => {
                    setThumbnailFile(null);
                    setThumbnailPreview(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => thumbnailInputRef.current?.click()}
                disabled={isProcessing}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload Thumbnail
              </Button>
            )}

            <input
              ref={thumbnailInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleThumbSelect}
              disabled={isProcessing}
            />
          </div>

          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={formData.title}
              onChange={(event) => setFormData((prev) => ({ ...prev, title: event.target.value }))}
              disabled={isProcessing}
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              rows={2}
              value={formData.description}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              disabled={isProcessing}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>JLPT Level</Label>
                {vttCues.length > 0 ? (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Sparkles className="h-3 w-3" />
                    Auto detect
                  </span>
                ) : null}
              </div>
              <MultiSelect
                options={JLPT_LEVELS.map((level) => ({ value: level, label: level }))}
                selected={[formData.jlpt_level]}
                onChange={(selected) => {
                  const first = (selected[0] as JlptLevel) || "N5";
                  setFormData((prev) => ({
                    ...prev,
                    jlpt_level: first,
                  }));
                }}
                placeholder="Select JLPT"
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <MultiSelect
                options={categoryOptions}
                selected={formData.categories}
                onChange={(selected) => {
                  const normalized = normalizeCategoryList(selected);
                  setFormData((prev) => ({
                    ...prev,
                    categories: normalized,
                    category: normalized[0] || "story",
                  }));
                }}
                allowCreate
                onCreateOption={createCategory}
                createText="Create"
                searchPlaceholder="Search or create"
                placeholder="Select category"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_published}
              onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_published: checked }))}
              disabled={isProcessing}
            />
            <Label>Publish after processing</Label>
          </div>

          <div className="flex gap-2">
            {step === "error" ? (
              <Button type="button" variant="outline" className="flex-1" onClick={() => setStep("idle")}>
                Retry
              </Button>
            ) : null}

            <Button
              type="button"
              className="flex-1"
              onClick={handleUpload}
              disabled={
                isProcessing ||
                !selectedFile ||
                !formData.title.trim() ||
                !vttFile ||
                vttCues.length === 0
              }
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Working...
                </>
              ) : (
                "Upload"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
